import {
    KeyRound,
    MapPin,
    Shield,
    Sparkles,
    Upload,
    UserPlus,
    Users,
} from "lucide-react";
import React from "react";
import { cn } from "../../lib/utils";
import { Badge } from "../ui/badge";

export type AdminTab = "users" | "roles" | "ai-skills" | "org-seeding" | "org-assigning" | "manage-venues";

interface AdminSidebarProps {
  activeTab: AdminTab;
  onTabChange: (tab: AdminTab) => void;
  pendingUsersCount?: number;
}

export const AdminSidebar: React.FC<AdminSidebarProps> = ({
  activeTab,
  onTabChange,
  pendingUsersCount = 0,
}) => {
  const tabs = [
    {
      id: "users" as AdminTab,
      label: "User Management",
      icon: Users,
      description: "Approve and manage users",
      badge: pendingUsersCount > 0 ? pendingUsersCount : undefined,
    },
    {
      id: "roles" as AdminTab,
      label: "Roles",
      icon: KeyRound,
      description: "Create and manage roles",
    },
    {
      id: "ai-skills" as AdminTab,
      label: "AI Skills",
      icon: Sparkles,
      description: "Manage AI skills & provider assignments",
    },
    {
      id: "org-seeding" as AdminTab,
      label: "Organisation Seeding",
      icon: Upload,
      description: "Bulk upload organisations via JSON/CSV",
    },
    {
      id: "org-assigning" as AdminTab,
      label: "Organisation Assigning",
      icon: UserPlus,
      description: "Assign admin users to organisations",
    },
    {
      id: "manage-venues" as AdminTab,
      label: "Manage Venues",
      icon: MapPin,
      description: "Add venues, generate QR codes, and manage access points",
    },
  ];

  return (
    <div className="w-64 bg-card border-r border-border h-full">
      <div className="p-6">
        <div className="flex items-center gap-2 mb-6">
          <Shield className="h-6 w-6 text-primary" />
          <h2 className="text-lg font-semibold">Admin Portal</h2>
        </div>

        <nav className="space-y-2">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;

            return (
              <button
                key={tab.id}
                onClick={() => onTabChange(tab.id)}
                className={cn(
                  "w-full flex items-center gap-3 px-3 py-2 rounded-lg text-left transition-colors",
                  isActive
                    ? "bg-primary text-primary-foreground"
                    : "hover:bg-muted text-muted-foreground hover:text-foreground"
                )}
              >
                <Icon className="h-5 w-5" />
                <div className="flex-1">
                  <div className="font-medium">{tab.label}</div>
                  <div
                    className={cn(
                      "text-xs",
                      isActive
                        ? "text-primary-foreground/70"
                        : "text-muted-foreground"
                    )}
                  >
                    {tab.description}
                  </div>
                </div>
                {tab.badge && (
                  <Badge
                    variant={isActive ? "secondary" : "default"}
                    className="text-xs"
                  >
                    {tab.badge}
                  </Badge>
                )}
              </button>
            );
          })}
        </nav>
      </div>
    </div>
  );
};
